<template>
  <div class="mfa-setup">
    <validation-observer
      ref="formValidation"
      v-slot="{ handleSubmit }"
      tag="div"
      class="mfa-setup__form"
    >
      <b-form
        novalidate
        @submit.stop.prevent="handleSubmit(completeMFASetup)"
      >
        <b-alert
          show
          variant="warning"
          class="mb-3"
        >
          <strong>Scan this QR code, even if you tried before.</strong>
          Use the new RingbaX entry in your app. Setup is complete only after you verify its code here.
        </b-alert>

        <BaseButtonLoader
          v-if="canRetryInitialization"
          id="button-retry-mfa-setup"
          data-cy="button-retry-mfa-setup"
          :loading="loading"
          button-class="custom-button--white-hover-grad mb-3"
          type="button"
          @click="initiateMFASetup"
        >
          Retry Setup
        </BaseButtonLoader>

        <canvas
          ref="canvas"
          class="my-2"
          role="img"
          aria-label="Scan this QR code with your authenticator app to add RingbaX"
        />

        <div class="mb-2">
          <div
            class="custom-form-input-label mr-2"
          >
            Secret Key
          </div>

          <div class="d-flex align-items-center">
            <div class="mfa-setup__secret-key pr-2">
              {{ MFASecretKey }}
            </div>

            <font-awesome-icon
              role="button"
              :icon="['fas', 'copy']"
              @click="copySecretKey(MFASecretKey)"
            />
          </div>
        </div>

        <BaseFormInput
          id="software-token"
          v-model="details.softwareToken"
          name="Software Token"
          placeholder="Enter the 6 digit MFA code with no whitespace"
          class="mt-4 d-block"
          label="Code"
          type="text"
          required
          width="12"
          width-lg="12"
          inputmode="numeric"
          autocomplete="one-time-code"
        >
          <template #helpText>
            <b-form-text>
              <div>
                Enter all 6 digits without spaces. Codes contain numbers only, so letter case does not apply. Click 'Verify Code' to complete setup.
              </div>
            </b-form-text>
          </template>
        </BaseFormInput>

        <BaseButtonLoader
          id="button-complete-mfa"
          data-cy="button-complete-mfa"
          :loading="verifyButtonLoading"
          :disabled="loading || !MFASecretKey"
          button-class="custom-button--white-hover-grad pulse mt-2"
          type="submit"
        >
          Verify Code
        </BaseButtonLoader>
      </b-form>
    </validation-observer>
    <MFASetupGuide />
  </div>
</template>
<script>
import { mapActions, mapState } from 'vuex'
import QRCode from 'qrcode'
import MFASetupGuide from './MFASetupGuide.vue'

import copyToClipboardMixin from '@/mixins/copy-to-clipboard-mixin'
import { showErrorMessage, showSuccessMessage } from '@/notification-utils'

export default {
  name: 'SoftwareMFA',

  title: 'Setup MFA',
  components: { MFASetupGuide },

  mixins: [copyToClipboardMixin],

  data() {
    return {
      loading: false,
      canRetryInitialization: false,
      verifyButtonLoading: false,
      details: {
        softwareToken: ''
      }
    }
  },
  computed: {
    ...mapState('auth', ['MFASecretKey', 'authenticatedUserDetails'])
  },
  async mounted() {
    await this.initiateMFASetup()
  },
  methods: {
    ...mapActions('auth', [
      'associateSoftwareMFA',
      'verifySoftwareMFA',
      'resetMFAKey',
      'getLoggedInUserDetails'
    ]),

    async initiateMFASetup() {
      try {
        this.loading = true
        this.canRetryInitialization = false
        await this.associateSoftwareMFA()
        const email = this.authenticatedUserDetails.email
        const value = `otpauth://totp/RingbaX:${email}?secret=${this.MFASecretKey}&issuer=RingbaX&algorithm=SHA1`
        await QRCode.toCanvas(this.$refs.canvas, value)
      } catch (error) {
        this.canRetryInitialization = true
        showErrorMessage(error.response?.data?.title || 'Could not start MFA setup. Try again.')
      } finally {
        this.loading = false
      }
    },

    async completeMFASetup() {
      try {
        this.verifyButtonLoading = true
        const softwareToken = this.details.softwareToken
        await this.verifySoftwareMFA(softwareToken)

        try {
          await this.getLoggedInUserDetails()
        } catch (error) {
          // MFA is already verified; the router guard refetches user details
          // on the next navigation, so don't fail the setup over this.
          console.error(error)
        }
        showSuccessMessage('Software MFA Setup Completed')
        this.resetMFAKey()

        this.$emit('success')
      } catch (error) {
        showErrorMessage(error.response?.data?.title || 'Check the code from the QR code on this page. Codes from an old RingbaX entry will not work.')
      } finally {
        this.verifyButtonLoading = false
      }
    },

    copySecretKey(secretKey) {
      this.copyTextToClipboard(secretKey)
      showSuccessMessage('Secret Key copied')
    }
  }
}
</script>
<style lang="scss" scoped>
.mfa-setup {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 2rem;
  align-items: start;
  &__form { min-width: 0; }
  canvas { max-width: 100%; height: auto !important; }
  &__secret-key {
    min-width: 0;
    overflow-wrap: anywhere;
  }
}

@include media-breakpoint-down(xs) {
  .mfa-setup {
    grid-template-columns: 1fr;
  }
}
</style>
